import React, { useEffect, useMemo, useState } from 'react';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Divider, message, Tooltip, Select, Tag, Alert } from 'antd';
import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from '../../../reducers';
import { AsyncDispatch } from '../../../../types/global';
import { booleanToInt } from '../../../utils/Utils';
import {
  updateUserAccountAssignments,
  fetchAccountBrands,
  fetchAccountRoles,
  fetchAdminUsersView,
} from '../../../actions/user';
import { MultiAccountUser, AccountAssignment, AccountBrand } from '../../../../types/user';
import { StandardResourceCode } from '../../../../types/resources';
import FormInput from '../../global/Forms/FormInput';
import FormSelect from '../../global/Forms/FormSelect';
import FormMultiSelect from '../../global/Forms/FormMultiSelect';
import DrawerHookForm from '../../global/drawer/DrawerHookForm';
import FormSwitch from '../../global/Forms/FormSwitch';
import AntTooltip from '../../global/AntTooltip';
import { hasPermission } from '../../../utils/Permissions';
import { useAppDispatch } from '../../../store/ReduxStore';

const { Option } = Select;

type MultiAccountUserDrawerProps = {
  visible: boolean;
  onClose: () => void;
  user?: MultiAccountUser;
};

const MultiAccountUserDrawer: React.FC<MultiAccountUserDrawerProps> = props => {
  const dispatch: AsyncDispatch = useAppDispatch();
  const { t } = useTranslation();

  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);

  const { adminAccounts, accountBrands, accountRoles, maintenanceTypes, canManageUsers } =
    useSelector(
      (state: ApplicationState) => ({
        adminAccounts: state.user.adminAccounts,
        accountBrands: state.user.accountBrands,
        accountRoles: state.user.accountRoles,
        maintenanceTypes: state.resources.data.global.maintenance_types as StandardResourceCode[],
        canManageUsers: hasPermission(state.user.user, 'can_manage_users') as boolean,
      }),
      shallowEqual
    );

  const extendedMaintenanceTypes = useMemo(
    () => [...maintenanceTypes, { id: 3, code: 'hide', name: 'Hide' }],
    [maintenanceTypes]
  );

  // Initialize selectedAccountId when drawer opens with a user
  useEffect(() => {
    if (
      props.visible &&
      props.user &&
      props.user.account_assignments.length > 0 &&
      !selectedAccountId
    ) {
      setSelectedAccountId(props.user.account_assignments[0].account_id);
    }
    // Reset selectedAccountId when drawer closes
    if (!props.visible) {
      setSelectedAccountId(null);
    }
  }, [props.visible, props.user, selectedAccountId]);

  // Load brands and roles for selected account
  useEffect(() => {
    if (selectedAccountId) {
      if (!accountBrands[selectedAccountId]) {
        dispatch(fetchAccountBrands(selectedAccountId));
      }
      if (!accountRoles[selectedAccountId]) {
        dispatch(fetchAccountRoles(selectedAccountId));
      }
    }
  }, [selectedAccountId, dispatch, accountBrands, accountRoles]);

  const selectedAccount = useMemo(() => {
    return adminAccounts.find(acc => acc.account_id === selectedAccountId);
  }, [selectedAccountId, adminAccounts]);

  const handleUserChange = async (values: any) => {
    const { user } = props;
    if (!user) return;

    // Build account assignments from form data
    const accountAssignments = values.accountAssignments.map((assignment: any) => ({
      account_id: assignment.account_id,
      admin: booleanToInt(assignment.admin)!,
      user_role_ids: assignment.user_role_ids || [],
      brand_permissions: Object.keys(assignment.brands || {})
        .filter(key => assignment.brands[Number(key)] !== 3) // Filter out "Hide"
        .map(key => ({
          brand_id: Number(key),
          maintenance_type_id: assignment.brands[Number(key)],
        })),
    }));

    try {
      const result = await dispatch(updateUserAccountAssignments(user.id, accountAssignments));

      // Check if the action was fulfilled
      if (result && result.type && result.type.endsWith('_FULFILLED')) {
        message.success(t('user:submit'));
        // Refetch the admin users list to show updated data
        await dispatch(fetchAdminUsersView());
        props.onClose();
      } else if (result && result.type && result.type.endsWith('_REJECTED')) {
        const errorMessage = result.payload?.response?.data?.message || t('common:errorOccurred');
        message.error(errorMessage);
        throw new Error(errorMessage);
      } else if (result?.value?.data || (result && !result.error)) {
        // Fallback: if result structure is different but no error, assume success
        message.success(t('user:submit'));
        // Refetch the admin users list to show updated data
        await dispatch(fetchAdminUsersView());
        props.onClose();
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || t('common:errorOccurred');
      message.error(errorMessage);
      throw error;
    }
  };

  const getInitialAccountAssignments = () => {
    if (!props.user) return [];

    return props.user.account_assignments.map(assignment => {
      const brands = accountBrands[assignment.account_id] || [];
      const brandPermissions = brands.reduce((obj, brand) => {
        const perm = assignment.brand_permissions.find(bp => bp.brand_id === brand.id);
        return {
          ...obj,
          [brand.id]: perm ? perm.maintenance_type_id : 3, // Default to Hide
        };
      }, {} as { [key: number]: number });

      return {
        account_id: assignment.account_id,
        admin: !!assignment.admin,
        user_role_ids: assignment.user_role_ids,
        brands: brandPermissions,
        has_activity: assignment.has_activity,
      };
    });
  };

  const initialValues = () => {
    return {
      firstName: props.user?.first_name || '',
      lastName: props.user?.last_name || '',
      email: props.user?.email || '',
      accountAssignments: getInitialAccountAssignments(),
    };
  };

  const handleAddAccountAssignment = (setValue: any, currentAssignments: any[]) => {
    // Find accounts not yet assigned
    const assignedAccountIds = currentAssignments.map(a => a.account_id);
    const availableAccounts = adminAccounts.filter(
      acc => !assignedAccountIds.includes(acc.account_id)
    );

    if (availableAccounts.length === 0) {
      message.info(t('user:noMoreAccountsToAdd'));
      return;
    }

    // Add first available account
    const newAccount = availableAccounts[0];
    const newAssignment = {
      account_id: newAccount.account_id,
      admin: false,
      user_role_ids: [],
      brands: {},
      has_activity: false,
    };

    setValue('accountAssignments', [...currentAssignments, newAssignment], {
      shouldDirty: true,
    });
    setSelectedAccountId(newAccount.account_id);
  };

  const handleRemoveAccountAssignment = (
    setValue: any,
    currentAssignments: any[],
    accountId: number
  ) => {
    const assignment = currentAssignments.find(a => a.account_id === accountId);
    if (assignment?.has_activity) {
      message.error(t('user:cannotRemoveAccountWithActivity'));
      return;
    }

    const newAssignments = currentAssignments.filter(a => a.account_id !== accountId);
    setValue('accountAssignments', newAssignments, { shouldDirty: true });

    if (selectedAccountId === accountId) {
      setSelectedAccountId(newAssignments.length > 0 ? newAssignments[0].account_id : null);
    }
  };

  const getMaintenanceTypeTooltip = (code: string) => {
    switch (code) {
      case 'read':
        return t('user:maintenanceTypeReadInfo');
      case 'edit':
        return t('user:maintenanceTypeEditInfo');
      case 'hide':
        return t('user:maintenanceTypeHideInfo');
      default:
        return '';
    }
  };

  return (
    <DrawerHookForm
      key={props.user?.id || 'new-user'}
      title={props.user ? `${props.user.first_name} ${props.user.last_name}` : t('user:addUser')}
      width={600}
      visible={props.visible}
      onClose={props.onClose}
      destroyOnClose
      onSubmit={values => handleUserChange(values)}
      handleSaveButtonEnabled={formState => canManageUsers && formState.isDirty}
      defaultValues={initialValues()}
      enableReinitialize
    >
      {({ setValue, watch }) => {
        const values = watch();
        const currentAssignments = values.accountAssignments || [];
        const selectedAssignmentIndex = currentAssignments.findIndex(
          (a: any) => a.account_id === selectedAccountId
        );
        const selectedAssignment =
          selectedAssignmentIndex >= 0 ? currentAssignments[selectedAssignmentIndex] : null;

        return (
          <Form className="user__settings-form" layout="vertical">
            <FormInput name="firstName" label={t('user:firstName')} useHookForm required />
            <FormInput name="lastName" label={t('user:lastName')} useHookForm required />
            <FormInput
              name="email"
              label={t('user:email')}
              required
              disabled={!!props.user}
              useHookForm
            />

            <Divider orientation="left">{t('user:accountAssignments')}</Divider>

            <Alert
              message={t('user:accountAssignmentsInfo')}
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              className="mb-4"
            />

            {/* Account Access Tags - displays all accounts user has access to */}
            <div className="mb-4">
              <div className="mb-2 font-medium">{t('user:accountAccess')}</div>
              <div className="flex flex-wrap gap-2">
                {currentAssignments.map((assignment: any) => {
                  const account = adminAccounts.find(
                    acc => acc.account_id === assignment.account_id
                  );
                  if (!account) return null;

                  return (
                    <Tag
                      key={assignment.account_id}
                      color={assignment.admin ? 'blue' : 'default'}
                      closable={!assignment.has_activity}
                      onClose={e => {
                        e.preventDefault();
                        handleRemoveAccountAssignment(
                          setValue,
                          currentAssignments,
                          assignment.account_id
                        );
                      }}
                      style={{ padding: '4px 8px' }}
                    >
                      {account.account_name}
                      {!!assignment.admin && <> (Admin)</>}
                      {assignment.has_activity && (
                        <Tooltip title={t('user:cannotRemoveAccountWithActivity')}>
                          <InfoCircleOutlined className="ml-1" />
                        </Tooltip>
                      )}
                    </Tag>
                  );
                })}
                {currentAssignments.length < adminAccounts.length && (
                  <Button
                    type="dashed"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddAccountAssignment(setValue, currentAssignments)}
                  >
                    {t('user:addAccountAssignment')}
                  </Button>
                )}
              </div>
            </div>

            {/* Account Selector - for selecting which account's permissions to edit */}
            {currentAssignments.length > 0 && (
              <>
                <Form.Item label={t('user:selectAccountToEditPermissions')}>
                  <Select
                    value={selectedAccountId}
                    onChange={setSelectedAccountId}
                    placeholder={t('user:selectAccountToManage')}
                    style={{ width: '100%' }}
                  >
                    {currentAssignments.map((assignment: any) => {
                      const account = adminAccounts.find(
                        acc => acc.account_id === assignment.account_id
                      );
                      return (
                        <Option key={assignment.account_id} value={assignment.account_id}>
                          {account?.account_name}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>

                {/* Per-Account Permissions */}
                {selectedAccount &&
                  selectedAssignment &&
                  selectedAssignmentIndex >= 0 &&
                  selectedAccountId !== null && (
                    <div className="border-l-2 border-blue-500 pl-4">
                      <div className="user__permission-row flex mb-4">
                        <div className="user__permission-name">
                          {t('user:adminForAccount', { accountName: selectedAccount.account_name })}
                          <span className="user__permission-info">
                            <AntTooltip title={t('user:adminInfo')}>
                              <InfoCircleOutlined />
                            </AntTooltip>
                          </span>
                        </div>
                        <FormSwitch
                          name={`accountAssignments.${selectedAssignmentIndex}.admin`}
                          checkedChildren={t('common:yes')}
                          unCheckedChildren={t('common:no')}
                          useHookForm
                        />
                      </div>

                      {!selectedAssignment.admin && (
                        <>
                          <FormMultiSelect
                            name={`accountAssignments.${selectedAssignmentIndex}.user_role_ids`}
                            values={accountRoles[selectedAccountId] || []}
                            label={t('user:rolesForAccount', {
                              accountName: selectedAccount.account_name,
                            })}
                            placeholder={t('user:selectRoles')}
                            useHookForm
                          />

                          <Divider orientation="left">
                            {t('user:brandsForAccount', {
                              accountName: selectedAccount.account_name,
                            })}
                          </Divider>

                          {(accountBrands[selectedAccountId] || []).map((brand: AccountBrand) => (
                            <div key={brand.id} className="user__permission-row flex">
                              <div className="user__permission-name">{brand.name}</div>
                              <div className="flex items-center">
                                <FormSelect
                                  name={`accountAssignments.${selectedAssignmentIndex}.brands.${brand.id}`}
                                  values={extendedMaintenanceTypes}
                                  useHookForm
                                />
                                <AntTooltip
                                  title={
                                    <div>
                                      {extendedMaintenanceTypes.map(type => (
                                        <div key={type.id} className="mb-1">
                                          <strong>{type.name}:</strong>{' '}
                                          {getMaintenanceTypeTooltip(type.code)}
                                        </div>
                                      ))}
                                    </div>
                                  }
                                >
                                  <InfoCircleOutlined className="ml-2 text-gray-400 cursor-help" />
                                </AntTooltip>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}

                {!selectedAccountId && (
                  <Alert message={t('user:noAccountSelected')} type="info" showIcon />
                )}
              </>
            )}

            {currentAssignments.length === 0 && (
              <Alert
                message={t('user:addAccountAssignment')}
                description={t('user:noAccountsAssigned')}
                type="warning"
                showIcon
                action={
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => handleAddAccountAssignment(setValue, currentAssignments)}
                  >
                    {t('user:addAccountAssignment')}
                  </Button>
                }
              />
            )}
          </Form>
        );
      }}
    </DrawerHookForm>
  );
};

export default MultiAccountUserDrawer;
